import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../common/utils';
import {
	isProcessingAllowedByPreferences,
	isProcessingAllowedByPreferencesMaybeSync,
} from '../../../common/utils/item-processing';
import { Logger } from '../../../common/utils/logger';
import { BSCIndexedDB } from '../../../services/indexed-db-service';
import { CategoryStorageType, Status, type SetItemResult } from '../../../types';
import { ItemKeyCache } from '../../item-key-cache-service';
import { WebStorageType } from '../types';

const isPromise = <T>(value: unknown): value is Promise<T> =>
	value != null && typeof (value as { then?: unknown }).then === 'function';

// SET COOKIE ANALOGUE
/**
 * Sets a WebStorage item asynchronously. This queries user preferences before setting
 * the item, so it must be used for any items which are not strictly necessary.
 */
export const setStorageItem = async (
	itemKey: string,
	value: string,
	storageType: WebStorageType,
	productName: string | undefined,
	browserStorage: Storage,
): Promise<SetItemResult> => {
	const categoryStorageType =
		storageType === WebStorageType.LocalStorage
			? CategoryStorageType.LocalStorage
			: CategoryStorageType.SessionStorage;
	// `disableWebStorageConsentChecks` skips the consent pipeline (analytics-only),
	// but `__UNSAFE_shouldEnforceKeyForRollout(key, type) === true` re-enables enforcement for this key.
	let disableWebStorageConsentChecks = getBscGlobalState().disableWebStorageConsentChecks ?? false;
	if (fg('jfp-magma-selective-storage-enforcement')) {
		const __UNSAFE_shouldEnforceKeyForRollout =
			getBscGlobalState().__UNSAFE_shouldEnforceKeyForRollout;
		const enforceThisKey =
			__UNSAFE_shouldEnforceKeyForRollout?.(itemKey, categoryStorageType) === true;
		disableWebStorageConsentChecks =
			(getBscGlobalState().disableWebStorageConsentChecks ?? false) && !enforceThisKey;
	}
	if (disableWebStorageConsentChecks) {
		const eventAttributes = {
			itemKey,
			storageType,
			consentChecksDisabled: true,
		};
		try {
			if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
				// note: at the time of writing, this doesn't actually do anything because browserStorage.setItem calls the original setItem, not the overridden one.
				// see this thread for more information: https://atlassian.slack.com/archives/C05U6KCDNQJ/p1778585948364219
				ItemKeyCache.markSetByPackage(categoryStorageType, itemKey);
				browserStorage.setItem(itemKey, value);
			} else {
				browserStorage.setItem(itemKey, value);
				await BSCIndexedDB.setRecord(itemKey);
			}
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedAtlBrowserStorageSetItemError',
				message: `${storageType} failed to setItem with consent checks disabled. ${e.message || ''}`,
				attributes: eventAttributes,
			});
			return { status: Status.ERROR, error: e };
		}
		let wasAsync: boolean = true;
		try {
			let shouldSetItem: boolean;
			if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
				const shouldSetItemOrPromise = isProcessingAllowedByPreferencesMaybeSync({
					itemKey,
					storageType: categoryStorageType,
					productName,
				});
				if (isPromise(shouldSetItemOrPromise)) {
					shouldSetItem = await shouldSetItemOrPromise;
				} else {
					wasAsync = false;
					shouldSetItem = shouldSetItemOrPromise;
				}
			} else {
				shouldSetItem = await isProcessingAllowedByPreferences({
					itemKey,
					storageType: categoryStorageType,
					productName,
				});
			}

			sendPackageOperationalEvent({
				action: 'usedAtlBrowserStorageSetItem',
				attributes: {
					...eventAttributes,
					wasRejected: !shouldSetItem,
					wasAsync,
				},
			});
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedAtlBrowserStorageSetItemError',
				attributes: {
					...eventAttributes,
					wasAsync,
				},
				message: `Failed to emit analytics for setStorageItem with consent checks disabled. ${e.message || ''}`,
			});
		}

		return { status: Status.SUCCESS };
	}
	// user belongs to an org that opted into cookies management and we should respect their preferences
	let shouldSetItem: boolean;
	let wasAsync: boolean = true;
	if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
		const shouldSetItemOrPromise = isProcessingAllowedByPreferencesMaybeSync({
			itemKey,
			storageType: categoryStorageType,
			productName,
		});
		if (isPromise(shouldSetItemOrPromise)) {
			shouldSetItem = await shouldSetItemOrPromise;
		} else {
			wasAsync = false;
			shouldSetItem = shouldSetItemOrPromise;
		}
	} else {
		// user belongs to an org that opted into cookies management and we should respect their preferences
		shouldSetItem = await isProcessingAllowedByPreferences({
			itemKey,
			storageType: categoryStorageType,
			productName,
		});
	}

	const eventAttributes = {
		wasRejected: !shouldSetItem,
		itemKey,
		storageType,
		consentChecksDisabled: false,
		wasAsync,
	};
	try {
		sendPackageOperationalEvent({
			action: 'usedAtlBrowserStorageSetItem',
			attributes: eventAttributes,
		});

		if (shouldSetItem) {
			if (fg('jfp-magma-no-unnecessary-awaits-in-bsc')) {
				// note: at the time of writing, this doesn't actually do anything because browserStorage.setItem calls the original setItem, not the overridden one.
				// see this thread for more information: https://atlassian.slack.com/archives/C05U6KCDNQJ/p1778585948364219
				ItemKeyCache.markSetByPackage(categoryStorageType, itemKey);
				browserStorage.setItem(itemKey, value);
				return { status: Status.SUCCESS };
			} else {
				// Store the item as a record in our IDB service to track that we've seen it before.
				// That way, when we can confirm/deny that this has been set by our service when we check again.
				// If it fails for some reason, it will be caught before actually setting the item
				await BSCIndexedDB.setRecord(itemKey);
				browserStorage.setItem(itemKey, value);
				return { status: Status.SUCCESS };
			}
		} else {
			return { status: Status.BLOCKED };
		}
	} catch (e: any) {
		Logger.errorWithOperationalEvent({
			action: 'usedAtlBrowserStorageSetItemError',
			attributes: eventAttributes,
			message: `Failed to set ${storageType} item. ${e.message || ''}`,
		});
		return { status: Status.ERROR, error: e };
	}
};
